/**
 * Board View
 *
 * Renders the shared kanban board used across Spaces surfaces.
 *
 * Primary responsibilities:
 * - Group tasks into board columns
 * - Support drag-and-drop reordering and cross-column moves
 * - Keep board cards visually scannable and compact
 *
 * Dependencies:
 * - dnd-kit drag and drop primitives
 * - db/space-tasks mutation helpers
 */

'use client'

import { FC, MouseEvent, memo, useCallback, useMemo, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { DndContext, DragEndEvent, useDroppable } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  IconPlus,
  IconGripVertical,
  IconCalendar,
  IconClock,
  IconFlag,
  IconPaperclip,
  IconMessages,
  IconEye
} from '@tabler/icons-react'
import { format, isPast, isToday, isTomorrow } from 'date-fns'
import { updateTask, updateTaskPosition, updateTaskStatus } from '@/db/space-tasks'
import { toast } from 'sonner'
import type { SpaceBoardColumn } from '@/lib/spaces/board-columns'
import type { StatusTemplate } from '@/types/view-templates'
import { cn } from '@/lib/utils'
import {
  buildLosiSyntheticTaskProfile,
  isLosiTaskAssigneeId
} from '@/lib/spaces/losi-task-assignee'
import {
  TaskStatusIcon,
  getTaskStatusIconToneClass,
  resolveTaskStatusIconName,
  type TaskStatusIconName
} from '../tasks/task-status-icon'
import { StatusChangeButton } from '../tasks/status-change-button'
import {
  SpaceEmptyState,
  spaceCompactActionTargetClass,
  spaceRepeatedCardClass,
  spaceRepeatedCardInteractiveClass
} from '@/components/spaces/shared/space-design'

type TaskProgressItem = {
  completed?: unknown
  done?: unknown
  is_completed?: unknown
  checked?: unknown
  status?: unknown
}

type TaskAssignee = {
  id?: string | null
  user_id?: string | null
  image_url?: string | null
  avatar_url?: string | null
  display_name?: string | null
  name?: string | null
  username?: string | null
  email?: string | null
}

interface Task {
  id: string
  space_id?: string
  title: string
  description?: string
  status: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  due_date?: string
  assigned_to?: string
  assigned_to_name?: string
  assigned_to_avatar?: string
  time_estimate_minutes?: number
  time_spent_minutes?: number
  folder_id?: string
  sprint_id?: string
  position?: number | null
  version?: number | null
  created_at?: string
  tags?: string[]
  watchers?: string[]
  attachments_count?: number
  comments_count?: number
  checklist_items?: number | string | null
  checklist_completed?: number | string | null
  subtask_items?: number | string | null
  subtask_completed?: number | string | null
  completion_total?: number | string | null
  completion_done?: number | string | null
  completion_percent?: number | string | null
  assignee?: {
    image_url?: string | null
    display_name?: string | null
    username?: string | null
    email?: string | null
  } | null
  assignee_ids?: string[] | null
  assignees?: TaskAssignee[] | null
  custom_fields?: unknown
  checklist?: TaskProgressItem[] | { items?: TaskProgressItem[] } | null
  subtasks?: TaskProgressItem[] | { items?: TaskProgressItem[] } | null
}

interface BoardViewProps {
  tasks: Task[]
  columns?: SpaceBoardColumn[]
  onTaskUpdate?: (task: Task) => void
  onTaskClick?: (task: Task) => void
  onCreateTask?: (status: string) => void
  spaceId: string
  isReorderEnabled?: boolean
  statusTemplate?: StatusTemplate | null
}

interface SortableTaskCardProps {
  task: Task
  onTaskClick?: (task: Task) => void
  onTaskStatusChange: (taskId: string, status: string) => Promise<void>
  statusOptions: string[]
  statusTemplate?: StatusTemplate | null
}

interface BoardColumnViewProps {
  column: SpaceBoardColumn
  tasks: Task[]
  isSortedAZ: boolean
  onColumnTitleClick?: (columnId: string) => void
  onTaskClick?: (task: Task) => void
  onTaskStatusChange: (taskId: string, status: string) => Promise<void>
  onCreateTask?: (status: string) => void
  statusOptions: string[]
  statusTemplate?: StatusTemplate | null
}

const defaultColumns: SpaceBoardColumn[] = [
  {
    id: 'todo',
    title: 'To Do',
    status: 'todo',
    statuses: ['todo', 'pending', 'backlog', 'new'],
    color: 'bg-gray-100 dark:bg-gray-900/30'
  },
  {
    id: 'in_progress',
    title: 'In Progress',
    status: 'in_progress',
    statuses: ['in_progress', 'inprogress', 'doing', 'active', 'started'],
    color: 'bg-blue-100 dark:bg-blue-900/30'
  },
  {
    id: 'done',
    title: 'Done',
    status: 'done',
    statuses: ['done', 'completed', 'finished'],
    color: 'bg-green-100 dark:bg-green-900/30'
  }
]

const normalizeStatusValue = (status: string) => {
  return status.toLowerCase().replace(/[-\s]/g, '_')
}

export type BoardColumnStatusIconName =
  TaskStatusIconName

export const getBoardColumnStatusIconName = (
  column: Pick<SpaceBoardColumn, 'status' | 'title'> & {
    statuses?: string[]
  }
): BoardColumnStatusIconName => {
  return resolveTaskStatusIconName({
    status: column.status,
    title: column.title,
    statuses: column.statuses
  })
}

const renderBoardColumnStatusIcon = (
  iconName: BoardColumnStatusIconName
) => {
  return (
    <TaskStatusIcon
      iconName={iconName}
      className={cn('mt-1', getTaskStatusIconToneClass(iconName))}
      testId={`board-column-status-icon-${iconName}`}
    />
  )
}

const getPriorityAccent = (priority: string) => {
  switch (priority) {
    case 'urgent':
      return {
        badge:
          'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200'
      }
    case 'high':
      return {
        badge:
          'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900/50 dark:bg-orange-950/30 dark:text-orange-200'
      }
    case 'medium':
      return {
        badge:
          'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200'
      }
    case 'low':
      return {
        badge:
          'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200'
      }
    default:
      return {
        badge:
          'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-200'
      }
  }
}

const getPriorityIcon = (priority: string) => {
  const color =
    priority === 'urgent'
      ? 'text-red-500'
      : priority === 'high'
        ? 'text-orange-500'
        : priority === 'medium'
          ? 'text-yellow-500'
          : 'text-green-500'

  return <IconFlag size={12} className={color} />
}

const getColumnTone = (colorClass: string) => {
  if (colorClass.includes('blue')) {
    return {
      dot: 'text-blue-500',
      header: 'border-slate-200/80 bg-slate-50/85 dark:border-slate-800/80 dark:bg-white/[0.02]',
      count:
        'border-slate-200 bg-white/95 text-slate-700 dark:border-slate-700 dark:bg-black/40 dark:text-slate-200',
      subtext: 'text-slate-500 dark:text-slate-400'
    }
  }

  if (colorClass.includes('green')) {
    return {
      dot: 'text-emerald-500',
      header: 'border-slate-200/80 bg-slate-50/85 dark:border-slate-800/80 dark:bg-white/[0.02]',
      count:
        'border-slate-200 bg-white/95 text-slate-700 dark:border-slate-700 dark:bg-black/40 dark:text-slate-200',
      subtext: 'text-slate-500 dark:text-slate-400'
    }
  }

  if (colorClass.includes('yellow') || colorClass.includes('amber')) {
    return {
      dot: 'text-amber-500',
      header: 'border-slate-200/80 bg-slate-50/85 dark:border-slate-800/80 dark:bg-white/[0.02]',
      count:
        'border-slate-200 bg-white/95 text-slate-700 dark:border-slate-700 dark:bg-black/40 dark:text-slate-200',
      subtext: 'text-slate-500 dark:text-slate-400'
    }
  }

  if (colorClass.includes('orange')) {
    return {
      dot: 'text-orange-500',
      header: 'border-slate-200/80 bg-slate-50/85 dark:border-slate-800/80 dark:bg-white/[0.02]',
      count:
        'border-slate-200 bg-white/95 text-slate-700 dark:border-slate-700 dark:bg-black/40 dark:text-slate-200',
      subtext: 'text-slate-500 dark:text-slate-400'
    }
  }

  if (colorClass.includes('red')) {
    return {
      dot: 'text-red-500',
      header: 'border-slate-200/80 bg-slate-50/85 dark:border-slate-800/80 dark:bg-white/[0.02]',
      count:
        'border-slate-200 bg-white/95 text-slate-700 dark:border-slate-700 dark:bg-black/40 dark:text-slate-200',
      subtext: 'text-slate-500 dark:text-slate-400'
    }
  }

  if (colorClass.includes('purple') || colorClass.includes('pink')) {
    return {
      dot: 'text-violet-500',
      header: 'border-slate-200/80 bg-slate-50/85 dark:border-slate-800/80 dark:bg-white/[0.02]',
      count:
        'border-slate-200 bg-white/95 text-slate-700 dark:border-slate-700 dark:bg-black/40 dark:text-slate-200',
      subtext: 'text-slate-500 dark:text-slate-400'
    }
  }

  return {
    dot: 'text-slate-500',
    header: 'border-slate-200/80 bg-slate-50/85 dark:border-slate-800/80 dark:bg-white/[0.02]',
    count:
      'border-slate-200 bg-white/95 text-slate-700 dark:border-slate-700 dark:bg-black/40 dark:text-slate-200',
    subtext: 'text-slate-500 dark:text-slate-400'
  }
}

const getPlainTextPreview = (content: string) => {
  const withoutHtml = content.replace(/<[^>]*>/g, ' ')
  const withoutMarkdown = withoutHtml
    .replace(/!\[[^\]]*\]\([^)]+\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[`*_>#~-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return withoutMarkdown
}

const getDueDateMeta = (task: Task) => {
  if (!task.due_date) {
    return null
  }

  const dueDate = new Date(task.due_date)
  const normalizedStatus = normalizeStatusValue(task.status || 'todo')
  const isCompleted = normalizedStatus === 'done'

  let label = format(dueDate, 'MMM d')
  let className =
    'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-800 dark:bg-slate-900/50 dark:text-slate-300'

  if (!isCompleted && isPast(dueDate) && !isToday(dueDate)) {
    label = `Overdue ${format(dueDate, 'MMM d')}`
    className =
      'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200'
  } else if (isToday(dueDate)) {
    label = 'Today'
    className =
      'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200'
  } else if (isTomorrow(dueDate)) {
    label = 'Tomorrow'
    className =
      'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/40 dark:text-blue-200'
  }

  return {
    className,
    label
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const toSafeCount = (value: unknown) => {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? 0 : Math.max(0, value)
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim()
    if (!trimmedValue) {
      return 0
    }

    const parsedValue = Number(trimmedValue)
    return Number.isFinite(parsedValue) ? Math.max(0, parsedValue) : 0
  }

  return 0
}

const toOptionalCount = (value: unknown) => {
  if (typeof value === 'number') {
    if (Number.isNaN(value)) {
      return null
    }

    return Math.max(0, value)
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim()
    if (!trimmedValue) {
      return null
    }

    const parsedValue = Number(trimmedValue)
    if (Number.isFinite(parsedValue)) {
      return Math.max(0, parsedValue)
    }
  }

  return null
}

const getFirstCount = (...values: unknown[]) => {
  for (const value of values) {
    const count = toOptionalCount(value)
    if (count !== null) {
      return count
    }
  }

  return null
}

const isTaskItemCompleted = (item: TaskProgressItem) => {
  const completionSignals = [
    item.completed,
    item.done,
    item.is_completed,
    item.checked
  ]

  for (const signal of completionSignals) {
    if (typeof signal === 'boolean') {
      return signal
    }

    if (typeof signal === 'number') {
      return signal > 0
    }

    if (typeof signal === 'string') {
      const normalizedSignal = signal.trim().toLowerCase()
      if (
        [
          'true',
          '1',
          'yes',
          'checked',
          'done',
          'completed',
          'complete'
        ].includes(normalizedSignal)
      ) {
        return true
      }
      if (['false', '0', 'no', 'unchecked', 'todo', 'in_progress'].includes(normalizedSignal)) {
        return false
      }
    }
  }

  if (typeof item.status === 'string') {
    return resolveTaskStatusIconName({ status: item.status }) === 'done'
  }

  return false
}

const getProgressItems = (
  value: TaskProgressItem[] | { items?: TaskProgressItem[] } | null | undefined
) => {
  if (Array.isArray(value)) {
    return value
  }

  if (isRecord(value) && Array.isArray(value.items)) {
    return value.items
  }

  return []
}

const summarizeProgressItems = (items: TaskProgressItem[]) => {
  if (items.length === 0) {
    return { done: 0, total: 0 }
  }

  const done = items.reduce((completedCount, item) => {
    return completedCount + (isTaskItemCompleted(item) ? 1 : 0)
  }, 0)

  return {
    done,
    total: items.length
  }
}

const getTaskProgressMeta = (task: Task) => {
  const customFields = isRecord(task.custom_fields) ? task.custom_fields : null
  const checklistItemsSummary = summarizeProgressItems(getProgressItems(task.checklist))
  const subtaskItemsSummary = summarizeProgressItems(getProgressItems(task.subtasks))

  const checklistTotal =
    getFirstCount(
      task.checklist_items,
      customFields?.checklist_items,
      customFields?.checklist_total,
      customFields?.checklist_count
    ) ?? checklistItemsSummary.total
  const checklistDoneRaw =
    getFirstCount(
      task.checklist_completed,
      customFields?.checklist_completed,
      customFields?.checklist_done,
      customFields?.checklist_completed_count
    ) ?? checklistItemsSummary.done
  const checklistDone =
    checklistTotal > 0 ? Math.min(checklistTotal, checklistDoneRaw) : checklistDoneRaw

  const subtaskTotal =
    getFirstCount(
      task.subtask_items,
      customFields?.subtask_items,
      customFields?.subtask_total,
      customFields?.subtask_count
    ) ?? subtaskItemsSummary.total
  const subtaskDoneRaw =
    getFirstCount(
      task.subtask_completed,
      customFields?.subtask_completed,
      customFields?.subtask_done,
      customFields?.subtask_completed_count
    ) ?? subtaskItemsSummary.done
  const subtaskDone =
    subtaskTotal > 0 ? Math.min(subtaskTotal, subtaskDoneRaw) : subtaskDoneRaw

  const completionTotal =
    getFirstCount(
      task.completion_total,
      customFields?.completion_total,
      customFields?.completion_count
    ) ?? checklistTotal + subtaskTotal
  const completionDoneRaw =
    getFirstCount(
      task.completion_done,
      customFields?.completion_done,
      customFields?.completed_items
    ) ?? checklistDone + subtaskDone
  const completionDone =
    completionTotal > 0 ? Math.min(completionTotal, completionDoneRaw) : completionDoneRaw
  const completionPercent =
    getFirstCount(
      task.completion_percent,
      customFields?.completion_percent,
      customFields?.completion_percentage,
      customFields?.progress_percent
    ) ??
    (completionTotal === 0
      ? 0
      : Math.round((completionDone / completionTotal) * 100))

  return {
    checklistDone,
    checklistTotal,
    completionDone,
    completionPercent: Math.min(100, completionPercent),
    completionTotal,
    hasProgress:
      completionTotal > 0 ||
      completionDone > 0 ||
      checklistTotal > 0 ||
      subtaskTotal > 0 ||
      completionPercent > 0,
    subtaskDone,
    subtaskTotal
  }
}

const normalizeAssigneeId = (value: unknown) => {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null
}

const parseSortableDate = (value: string | undefined) => {
  if (!value) {
    return null
  }

  const parsedTime = new Date(value).getTime()
  return Number.isFinite(parsedTime) ? parsedTime : null
}

const sortTasksByDueDateThenCreated = (leftTask: Task, rightTask: Task) => {
  const leftDueTime = parseSortableDate(leftTask.due_date)
  const rightDueTime = parseSortableDate(rightTask.due_date)

  if (leftDueTime !== null && rightDueTime !== null && leftDueTime !== rightDueTime) {
    return leftDueTime - rightDueTime
  }

  if (leftDueTime !== null && rightDueTime === null) {
    return -1
  }

  if (leftDueTime === null && rightDueTime !== null) {
    return 1
  }

  const leftCreatedTime = parseSortableDate(leftTask.created_at)
  const rightCreatedTime = parseSortableDate(rightTask.created_at)

  if (
    leftCreatedTime !== null &&
    rightCreatedTime !== null &&
    leftCreatedTime !== rightCreatedTime
  ) {
    return leftCreatedTime - rightCreatedTime
  }

  if (leftCreatedTime !== null && rightCreatedTime === null) {
    return -1
  }

  if (leftCreatedTime === null && rightCreatedTime !== null) {
    return 1
  }

  return (leftTask.title || '').localeCompare(rightTask.title || '', undefined, {
    sensitivity: 'base',
    numeric: true
  })
}

const sortTasksAlphabetically = (leftTask: Task, rightTask: Task) => {
  const leftTitle = (leftTask.title || '').trim()
  const rightTitle = (rightTask.title || '').trim()

  return leftTitle.localeCompare(rightTitle, undefined, {
    sensitivity: 'base',
    numeric: true
  })
}

type TaskCardAssignee = {
  key: string
  label: string
  imageUrl?: string
}

const getTaskAssigneeLabel = (assignee: TaskAssignee) => {
  return (
    assignee.display_name?.trim() ||
    assignee.name?.trim() ||
    assignee.username?.trim() ||
    assignee.email?.trim() ||
    null
  )
}

const getTaskCardAssignees = (task: Task): TaskCardAssignee[] => {
  const assigneeMap = new Map<string, TaskCardAssignee>()
  const assigneeAliasMap = new Map<string, string>()
  const orderedKeys: string[] = []

  const upsertAssignee = (input: {
    ids?: Array<string | null | undefined>
    label?: string | null
    imageUrl?: string | null
  }) => {
    const normalizedLabel =
      typeof input.label === 'string' ? input.label.trim() : ''
    const normalizedIds = (input.ids || [])
      .map((candidateId) => normalizeAssigneeId(candidateId))
      .filter((candidateId): candidateId is string => Boolean(candidateId))
    const shouldUseLosiProfile =
      normalizedIds.some((candidateId) => isLosiTaskAssigneeId(candidateId)) ||
      (normalizedIds.length === 0 && isLosiTaskAssigneeId(normalizedLabel))
    const losiProfile = shouldUseLosiProfile
      ? buildLosiSyntheticTaskProfile()
      : null
    const displayIds = losiProfile
      ? Array.from(new Set([...normalizedIds, losiProfile.user_id || 'losi']))
      : normalizedIds
    const displayLabel = losiProfile?.display_name || normalizedLabel
    const displayImageUrl = losiProfile?.image_url || input.imageUrl || null
    const existingKey =
      displayIds
        .map((candidateId) => assigneeAliasMap.get(candidateId))
        .find((candidateKey): candidateKey is string => Boolean(candidateKey)) ||
      null
    const key =
      existingKey ||
      (losiProfile ? losiProfile.user_id || 'losi' : null) ||
      displayIds[0] ||
      (displayLabel ? `label:${displayLabel.toLowerCase()}` : null)

    if (!key) {
      return
    }

    const existingAssignee = assigneeMap.get(key)

    displayIds.forEach((candidateId) => {
      assigneeAliasMap.set(candidateId, key)
    })

    if (!orderedKeys.includes(key)) {
      orderedKeys.push(key)
    }

    assigneeMap.set(key, {
      key,
      label: displayLabel || existingAssignee?.label || 'User',
      imageUrl: displayImageUrl || existingAssignee?.imageUrl || undefined
    })
  }

  upsertAssignee({
    ids: [task.assigned_to || null],
    imageUrl: task.assigned_to_avatar || task.assignee?.image_url || null,
    label:
      task.assigned_to_name ||
      task.assignee?.display_name ||
      task.assignee?.username ||
      task.assignee?.email ||
      null
  })

  task.assignees?.forEach((assignee) => {
    upsertAssignee({
      ids: [assignee.id, assignee.user_id],
      imageUrl: assignee.image_url || assignee.avatar_url || null,
      label: getTaskAssigneeLabel(assignee)
    })
  })

  const assigneeIds = [
    ...(Array.isArray(task.assignee_ids) ? task.assignee_ids : []),
    ...(isRecord(task.custom_fields) && Array.isArray(task.custom_fields.assignee_ids)
      ? task.custom_fields.assignee_ids.filter(
          (candidate): candidate is string =>
            typeof candidate === 'string' && candidate.trim().length > 0
        )
      : [])
  ]

  assigneeIds.forEach((assigneeId) => {
    upsertAssignee({ ids: [assigneeId] })
  })

  return orderedKeys
    .map((key) => assigneeMap.get(key))
    .filter((assignee): assignee is TaskCardAssignee => Boolean(assignee))
}

const SortableTaskCard: FC<SortableTaskCardProps> = memo(({
  task,
  onTaskClick,
  onTaskStatusChange,
  statusOptions,
  statusTemplate
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }
  const progress = getTaskProgressMeta(task)
  const priorityAccent = getPriorityAccent(task.priority || 'medium')
  const dueDateMeta = getDueDateMeta(task)
  const preview = task.description ? getPlainTextPreview(task.description) : ''
  const assignees = getTaskCardAssignees(task)
  const visibleAssignees = assignees.slice(0, 4)
  const hiddenAssigneeCount = Math.max(0, assignees.length - visibleAssignees.length)
  const showCompletionBar =
    progress.completionTotal > 0 || progress.completionPercent > 0
  const handleProgressBadgeClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onTaskClick?.(task)
  }
  // eslint-disable-next-line tailwindcss/classnames-order
  const taskCardClassName = cn(
    spaceRepeatedCardClass,
    spaceRepeatedCardInteractiveClass,
    'mb-3 dark:bg-black/45',
    'hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(15,23,42,0.08)]',
    isDragging && 'rotate-1 opacity-70 shadow-[0_22px_40px_rgba(15,23,42,0.12)]'
  )
  // eslint-disable-next-line tailwindcss/classnames-order
  const dragHandleClassName = cn(
    spaceCompactActionTargetClass,
    'absolute right-3 top-3 flex items-center justify-center rounded-full border border-slate-200 bg-slate-50/90 p-0',
    'cursor-grab text-muted-foreground opacity-100 transition-all active:cursor-grabbing md:border-transparent md:bg-transparent md:opacity-0',
    'group-hover:border-slate-200 group-hover:bg-slate-50/90 group-hover:opacity-100',
    'dark:group-hover:border-slate-700 dark:group-hover:bg-white/[0.04]'
  )

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={taskCardClassName}
      onClick={() => {
        if (!isDragging) {
          onTaskClick?.(task)
        }
      }}
    >
      <CardContent className="group min-w-0 p-3 sm:p-4">
        <div
          {...listeners}
          className={dragHandleClassName}
        >
          <IconGripVertical size={15} />
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-1.5 pr-10 md:pr-8">
          <Badge
            variant="outline"
            className={`h-6 rounded-full px-2.5 text-[11px] font-medium ${priorityAccent.badge}`}
          >
            {getPriorityIcon(task.priority || 'medium')}
            <span className="ml-1 capitalize">{task.priority || 'medium'}</span>
          </Badge>

          <StatusChangeButton
            currentStatus={task.status || 'todo'}
            onStatusChange={(nextStatus) =>
              onTaskStatusChange(task.id, nextStatus)
            }
            size="sm"
            showLabel={true}
            availableStatuses={statusOptions}
            statusTemplate={statusTemplate}
            className="min-w-0 max-w-full"
            triggerTestId="board-task-status-badge"
            iconTestIdPrefix="board-task-status-icon"
          />

          {task.tags?.slice(0, 2).map((tag) => (
            <Badge
              key={tag}
              variant="outline"
              className="h-6 max-w-full truncate rounded-full border-slate-200 bg-slate-50 px-2.5 text-[11px] font-medium text-slate-600 dark:border-slate-700 dark:bg-white/[0.03] dark:text-slate-200"
            >
              {tag}
            </Badge>
          ))}

          {task.tags && task.tags.length > 2 && (
            <Badge
              variant="outline"
              className="h-6 rounded-full border-slate-200 bg-transparent px-2.5 text-[11px] font-medium text-slate-500 dark:border-slate-700 dark:text-slate-300"
            >
              +{task.tags.length - 2}
            </Badge>
          )}
        </div>

        <div
          data-testid="board-task-title"
          className="mb-2 pr-10 text-[15px] font-semibold leading-6 tracking-[-0.02em] text-slate-900 md:pr-8 dark:text-slate-100"
        >
          {task.title}
        </div>

        {preview && (
          <div className="mb-3 line-clamp-3 text-[12.5px] leading-5 text-slate-600 dark:text-slate-300">
            {preview}
          </div>
        )}

        {progress.hasProgress && (
          <div className="mb-4 rounded-[18px] border border-slate-200/80 bg-slate-50/80 p-2.5 dark:border-slate-800 dark:bg-white/[0.03]">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  Progress
                </div>
                {progress.completionTotal > 0 ? (
                  <div className="mt-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300">
                    {progress.completionDone} of {progress.completionTotal} complete
                  </div>
                ) : progress.completionPercent > 0 ? (
                  <div className="mt-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300">
                    {progress.completionPercent}% complete
                  </div>
                ) : (
                  <div className="mt-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300">
                    Tracking checklist and subtasks
                  </div>
                )}
              </div>

              {showCompletionBar && (
                <button
                  type="button"
                  data-testid="board-task-progress-badge"
                  onClick={handleProgressBadgeClick}
                  className="inline-flex shrink-0 items-center rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700 transition-colors hover:border-slate-300 dark:border-slate-700 dark:bg-black/40 dark:text-slate-200 dark:hover:border-slate-600"
                >
                  {progress.completionPercent}%
                </button>
              )}
            </div>

            {showCompletionBar && (
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200/80 dark:bg-slate-800">
                <div
                  className="h-full rounded-full bg-slate-900 transition-all dark:bg-slate-100"
                  style={{
                    width: `${progress.completionPercent}%`
                  }}
                />
              </div>
            )}

            <div className="mt-2 flex flex-wrap gap-1.5">
              {progress.checklistTotal > 0 && (
                <button
                  type="button"
                  data-testid="board-task-checklist-badge"
                  onClick={handleProgressBadgeClick}
                  className="inline-flex items-center rounded-full border border-slate-200 bg-white/90 px-2 py-1 text-[10px] font-medium text-slate-600 transition-colors hover:border-slate-300 dark:border-slate-700 dark:bg-black/40 dark:text-slate-300 dark:hover:border-slate-600"
                >
                  {progress.checklistDone}/{progress.checklistTotal} checklist
                </button>
              )}

              {progress.subtaskTotal > 0 && (
                <button
                  type="button"
                  data-testid="board-task-subtask-badge"
                  onClick={handleProgressBadgeClick}
                  className="inline-flex items-center rounded-full border border-slate-200 bg-white/90 px-2 py-1 text-[10px] font-medium text-slate-600 transition-colors hover:border-slate-300 dark:border-slate-700 dark:bg-black/40 dark:text-slate-300 dark:hover:border-slate-600"
                >
                  {progress.subtaskDone}/{progress.subtaskTotal} subtasks
                </button>
              )}
            </div>
          </div>
        )}

        <div className="mt-auto flex flex-wrap items-end justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {dueDateMeta && (
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${dueDateMeta.className}`}
              >
                <IconCalendar size={12} />
                {dueDateMeta.label}
              </span>
            )}

            {task.time_estimate_minutes && (
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
                <IconClock size={12} />
                {Math.round(task.time_estimate_minutes / 60)}h
              </span>
            )}

            {task.attachments_count && task.attachments_count > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                <IconPaperclip size={12} />
                {task.attachments_count}
              </span>
            )}

            {task.comments_count && task.comments_count > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                <IconMessages size={12} />
                {task.comments_count}
              </span>
            )}

            {task.watchers && task.watchers.length > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                <IconEye size={12} />
                {task.watchers.length}
              </span>
            )}
          </div>

          {assignees.length > 0 && (
            <div
              aria-label={`Assignees: ${assignees.map((assignee) => assignee.label).join(', ')}`}
              className="flex items-center"
            >
              <div className="flex -space-x-2">
                {visibleAssignees.map((assignee) => (
                  <Avatar
                    key={assignee.key}
                    data-testid="board-task-assignee-avatar"
                    className="size-7 border-2 border-white shadow-sm dark:border-slate-950"
                  >
                    <AvatarImage src={assignee.imageUrl} />
                    <AvatarFallback className="text-xs">
                      {assignee.label.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                ))}
              </div>
              {hiddenAssigneeCount > 0 && (
                <span
                  data-testid="board-task-assignee-overflow"
                  className="ml-2 inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-slate-200 bg-white px-1.5 text-[10px] font-semibold text-slate-600 dark:border-slate-700 dark:bg-black/40 dark:text-slate-300"
                >
                  +{hiddenAssigneeCount}
                </span>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
})

SortableTaskCard.displayName = 'SortableTaskCard'

const BoardColumnView: FC<BoardColumnViewProps> = memo(({
  column,
  tasks,
  isSortedAZ,
  onColumnTitleClick,
  onTaskClick,
  onTaskStatusChange,
  onCreateTask,
  statusOptions,
  statusTemplate
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: column.id })
  const isOverLimit = Boolean(column.limit && tasks.length > column.limit)
  const tone = getColumnTone(column.color)
  const statusIconName = getBoardColumnStatusIconName(column)
  const taskIds = useMemo(
    () => tasks.map((task) => task.id),
    [tasks]
  )

  return (
    <div className="w-full max-w-[340px] shrink-0 snap-start xl:max-w-[360px]">
      <div className="min-w-0 overflow-hidden rounded-[28px] border border-slate-200/70 bg-slate-50/70 shadow-[0_8px_24px_rgba(15,23,42,0.04)] dark:border-slate-800/80 dark:bg-black/30">
        <div className={`border-b p-4 ${tone.header}`}>
          <div className="mb-3 flex min-w-0 items-start justify-between gap-3">
            <button
              type="button"
              onClick={() => onColumnTitleClick?.(column.id)}
              className="flex min-h-10 min-w-0 touch-manipulation items-start gap-2 text-left transition-colors sm:min-h-0"
              title={isSortedAZ ? 'Sorted A-Z. Click to restore due-date order' : 'Sort A-Z'}
            >
              {renderBoardColumnStatusIcon(statusIconName)}
              <div className="min-w-0">
                <div className="truncate text-[15px] font-semibold tracking-[-0.02em] text-slate-900 dark:text-slate-100">
                  {column.title}
                </div>
                <div className={`mt-0.5 text-[11px] font-medium ${tone.subtext}`}>
                  {isSortedAZ ? 'Alphabetical order' : 'Due date order'}
                </div>
              </div>
            </button>

            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {isSortedAZ && (
                <span className="rounded-full border border-slate-200 bg-white/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:border-slate-700 dark:bg-slate-950/70 dark:text-slate-300">
                  A-Z
                </span>
              )}
              <span
                className={`inline-flex min-w-[38px] items-center justify-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${tone.count} ${
                  isOverLimit
                    ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200'
                    : ''
                }`}
              >
                {tasks.length}
                {column.limit ? `/${column.limit}` : ''}
              </span>
            </div>
          </div>
        </div>

        <div
          ref={setNodeRef}
          className={`min-h-[420px] bg-transparent p-3.5 transition-colors sm:min-h-[560px] ${
            isOver ? 'bg-slate-100/70 dark:bg-white/[0.03]' : ''
          }`}
        >
          {isOverLimit && (
            <div className="mb-3 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-medium text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
              WIP limit exceeded. Move or finish work before adding more here.
            </div>
          )}

          <SortableContext
            items={taskIds}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {tasks.map((task) => (
                <SortableTaskCard
                  key={task.id}
                  task={task}
                  onTaskClick={onTaskClick}
                  onTaskStatusChange={onTaskStatusChange}
                  statusOptions={statusOptions}
                  statusTemplate={statusTemplate}
                />
              ))}
            </div>
          </SortableContext>

          {tasks.length === 0 && (
            <SpaceEmptyState
              title="Nothing here yet"
              description={`Drop a task into ${column.title.toLowerCase()} or create one from this lane.`}
              density="medium"
              tone="task"
              icon={<IconPlus size={18} />}
            />
          )}

          <button
            type="button"
            onClick={() => onCreateTask?.(column.status)}
            className="mt-3 flex min-h-11 w-full touch-manipulation items-center justify-center gap-2 rounded-[18px] border border-dashed border-slate-300 bg-white/70 p-3 text-sm font-medium text-slate-600 transition-colors hover:border-slate-400 hover:bg-white active:scale-[0.99] dark:border-slate-700 dark:bg-white/[0.02] dark:text-slate-300 dark:hover:border-slate-600 dark:hover:bg-white/[0.04]"
          >
            <IconPlus size={16} />
            Add task
          </button>
        </div>
      </div>
    </div>
  )
})

BoardColumnView.displayName = 'BoardColumnView'

export const BoardView: FC<BoardViewProps> = ({
  tasks,
  columns = defaultColumns,
  onTaskUpdate,
  onTaskClick,
  onCreateTask,
  spaceId,
  isReorderEnabled = true,
  statusTemplate
}) => {
  const [sortedColumnIds, setSortedColumnIds] = useState<string[]>([])

  const sortedColumnIdSet = useMemo(
    () => new Set(sortedColumnIds),
    [sortedColumnIds]
  )

  const toggleColumnSort = useCallback((columnId: string) => {
    setSortedColumnIds((currentColumnIds) => {
      if (currentColumnIds.includes(columnId)) {
        return currentColumnIds.filter((currentId) => currentId !== columnId)
      }

      return [...currentColumnIds, columnId]
    })
  }, [])

  const columnStatusEntries = useMemo(
    () =>
      columns.map((column) => {
        const statuses = column.statuses?.length ? column.statuses : [column.status]

        return {
          columnId: column.id,
          statuses,
          normalizedStatuses: statuses.map(normalizeStatusValue)
        }
      }),
    [columns]
  )

  const firstColumnId = columns[0]?.id || null

  const columnIdByStatus = useMemo(() => {
    const nextColumnIdByStatus = new Map<string, string>()

    columnStatusEntries.forEach((entry) => {
      entry.normalizedStatuses.forEach((status) => {
        if (!nextColumnIdByStatus.has(status)) {
          nextColumnIdByStatus.set(status, entry.columnId)
        }
      })
    })

    return nextColumnIdByStatus
  }, [columnStatusEntries])

  const getColumnIdForStatus = useCallback(
    (status: string) => {
      const normalizedTaskStatus = normalizeStatusValue(status || 'todo')

      return columnIdByStatus.get(normalizedTaskStatus) || firstColumnId
    },
    [columnIdByStatus, firstColumnId]
  )

  const statusOptions = useMemo(
    () =>
      Array.from(
        new Set(columnStatusEntries.flatMap((entry) => entry.statuses))
      ),
    [columnStatusEntries]
  )

  const tasksByColumnId = useMemo(() => {
    const grouped: Record<string, Task[]> = {}

    columns.forEach((column) => {
      grouped[column.id] = []
    })

    tasks.forEach((task) => {
      const columnId = getColumnIdForStatus(task.status)
      if (columnId) {
        if (!grouped[columnId]) {
          grouped[columnId] = []
        }

        grouped[columnId].push(task)
      }
    })

    // Order cards by persisted `position` so reorders stay reflected even if
    // parent state preserves array order.
    columns.forEach((column) => {
      if (sortedColumnIdSet.has(column.id)) {
        grouped[column.id] = (grouped[column.id] || []).slice().sort(sortTasksAlphabetically)
        return
      }

      grouped[column.id] = (grouped[column.id] || []).slice().sort((a, b) => {
        return sortTasksByDueDateThenCreated(a, b)
      })
    })

    return grouped
  }, [columns, getColumnIdForStatus, sortedColumnIdSet, tasks])

  const handleTaskStatusChange = useCallback(async (taskId: string, newStatus: string) => {
    const task = tasks.find((candidate) => candidate.id === taskId)

    try {
      await updateTaskStatus(taskId, newStatus, task?.space_id || spaceId)

      if (task) {
        onTaskUpdate?.({
          ...task,
          status: newStatus
        })
      }

      toast.success('Task status updated')
    } catch (error) {
      console.error('Error updating task status:', error)
      toast.error('Failed to update task status')
    }
  }, [onTaskUpdate, spaceId, tasks])

  const handleDragEnd = useCallback(async ({ active, over }: DragEndEvent) => {
    if (!over) return

    const task = tasks.find((candidate) => candidate.id === String(active.id))
    if (!task) return

    const sourceColumnId = getColumnIdForStatus(task.status)

    const destinationTask = tasks.find((candidate) => candidate.id === String(over.id))
    const destinationColumnId = columns.some((column) => column.id === String(over.id))
      ? String(over.id)
      : destinationTask
        ? getColumnIdForStatus(destinationTask.status)
        : null

    if (!sourceColumnId || !destinationColumnId) return

    // Same-column reorder: update persisted `position` values so the card
    // stays where it was dropped.
    if (sourceColumnId === destinationColumnId) {
      if (!isReorderEnabled) {
        toast.error('Clear active filters before reordering tasks')
        return
      }

      if (!destinationTask) return

      const getColumnTasks = (): Task[] => {
        return tasks
          .filter((t) => getColumnIdForStatus(t.status) === sourceColumnId)
          .slice()
          .sort(sortTasksByDueDateThenCreated)
      }

      const columnTasks = getColumnTasks()
      const activeIndex = columnTasks.findIndex((t) => t.id === task.id)
      const destinationIndex = columnTasks.findIndex((t) => t.id === destinationTask.id)

      if (activeIndex === -1 || destinationIndex === -1 || activeIndex === destinationIndex) {
        return
      }

      const nextOrder = columnTasks.slice()
      const [moved] = nextOrder.splice(activeIndex, 1)
      nextOrder.splice(destinationIndex, 0, moved)

      try {
        const updated = await Promise.all(
          nextOrder.map((t, idx) => {
            const nextPosition = idx + 1

            if (typeof t.version === 'number') {
              return updateTaskPosition(
                t.id,
                nextPosition,
                t.version,
                t.space_id || spaceId
              )
            }

            return updateTask(t.id, {
              space_id: t.space_id || spaceId,
              position: nextPosition
            })
          })
        )

        updated.forEach((updatedTask) => {
          onTaskUpdate?.(updatedTask as Task)
        })

        toast.success('Task order updated')
      } catch (error) {
        console.error('Error reordering tasks:', error)
        toast.error('Failed to reorder tasks')
      }

      return
    }

    const destinationColumn = columns.find((column) => column.id === destinationColumnId)
    if (!destinationColumn) return

    await handleTaskStatusChange(task.id, destinationColumn.status)
  }, [
    columns,
    getColumnIdForStatus,
    handleTaskStatusChange,
    isReorderEnabled,
    onTaskUpdate,
    spaceId,
    tasks
  ])

  return (
    <div className="board-view h-full min-w-0 overflow-x-hidden p-0 sm:p-4" onDragOver={(event) => event.preventDefault()}>
      <DndContext onDragEnd={handleDragEnd}>
        <div className="flex h-full max-w-full touch-pan-x snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain pb-2">
          {columns.map((column) => (
            <BoardColumnView
              key={column.id}
              column={column}
              tasks={tasksByColumnId[column.id] || []}
              isSortedAZ={sortedColumnIdSet.has(column.id)}
              onColumnTitleClick={toggleColumnSort}
              onTaskClick={onTaskClick}
              onTaskStatusChange={handleTaskStatusChange}
              onCreateTask={onCreateTask}
              statusOptions={statusOptions}
              statusTemplate={statusTemplate}
            />
          ))}
        </div>
      </DndContext>
    </div>
  )
}
